/**
 * AssociationEntityCard — read-only rendering of one association on a merged
 * create card. Each association is labeled with the RELATIONSHIP it represents
 * (Company / Contact / Stage / …) so it's clear what the created entity is being
 * linked to, then renders the target: a canonical stage badge for pipeline-stage
 * associations, otherwise the target entity card (hydrated from the CRM store
 * when it exists).
 *
 * Extracted from MergedCreateActionCard so both the read view and the edit view
 * (which falls back to it for associations without an editable picker) can
 * render it without a circular import.
 */

import { type ReactNode, useMemo } from 'react';

import { ObjectCard } from '@/lib/crm/object-cards/ObjectCard';
import { useCrmPreview } from '@/lib/crm/preview';

import type { MergedAssociation } from '../../lib/proposals/utils/actionMerging';
import { resolvableEntityId } from '../../lib/proposals/utils/resolvableEntityId';
import { ActionEntityPreview } from './ActionEntityPreview';
import { ActionStageBadge } from './ActionStageBadge';
import {
  deriveTypeFromAssociation,
  isStageAssociation,
} from './associationTargets';
import {
  ENTITY_CONTENT_REGISTRY,
  extractProperties,
} from './CreateActionCard.helpers';
import { ExcludedOverlay } from './ExcludedOverlay';

/**
 * Human relationship label per association-type suffix. Longer/more specific
 * suffixes (e.g. `_to_deal_pipeline_stage`) come first so they win over the
 * shorter ones (`_to_deal`).
 */
const RELATIONSHIP_LABELS: ReadonlyArray<readonly [string, string]> = [
  ['_to_deal_pipeline_stage', 'Stage'],
  ['_to_lifecycle_stage', 'Stage'],
  ['_to_company', 'Company'],
  ['_to_contact', 'Contact'],
  ['_to_owner', 'Owner'],
  ['_to_deal', 'Deal'],
  ['_to_risk', 'Risk'],
];

function getRelationshipLabel(associationType: string): string {
  // Any pipeline/lifecycle stage association reads as "Stage", including custom
  // entity types (e.g. org_bug_to_org_bug_pipeline_stage) whose suffix isn't in
  // the built-in list below — keeps the label consistent across all pipelinable
  // objects.
  if (isStageAssociation(associationType)) {
    return 'Stage';
  }
  for (const [suffix, label] of RELATIONSHIP_LABELS) {
    if (associationType.endsWith(suffix)) {
      return label;
    }
  }
  return 'Associated';
}

/**
 * Renders a single association: a relationship label + the target (stage badge
 * or entity card).
 */
export function AssociationEntityCard({
  assoc,
  orgId,
  sourceEntityType,
  compact = false,
  excluded = false,
  excludedMessage,
}: {
  assoc: MergedAssociation;
  orgId: number;
  /**
   * Entity type of the record the association hangs off (e.g.
   * 'ahoy_contact'). Scopes the stage badge's pipelines-schema lookup —
   * required for stage-only taxonomies (contact lifecycle stages), which
   * only surface in the per-entity-type pipelines listing.
   */
  sourceEntityType?: string;
  /**
   * Render the target as a one-line compact chip (avatar + name, no bordered
   * card) instead of a full entity card. Used when the target owns its own
   * top-level create card on the proposal.
   */
  compact?: boolean;
  /**
   * The association won't be applied: its action is unchecked, or it is
   * invalid because the target's create was unchecked (so the target won't
   * exist). Renders the target de-emphasised so the dropped link is never
   * silent. Pair with `compact` — a link that won't happen doesn't earn a
   * full card.
   */
  excluded?: boolean;
  /**
   * Why the link won't be applied, when `excluded`. Defaults to "the target
   * is unchecked", which is right for an unchecked link but wrong for an
   * INVALID one — there the missing entity may be the source, so the caller
   * passes the verdict's own sentence (see `MergedCreateActionCard`).
   */
  excludedMessage?: string;
}) {
  const assocEnriched = assoc.enrichedData;
  const relationshipLabel = getRelationshipLabel(assoc.associationType);

  const targetEntityType =
    assocEnriched?.target_object?.entity_type_name ??
    deriveTypeFromAssociation(assoc.associationType) ??
    assocEnriched?.entity_type_name ??
    '';

  const targetLabel =
    assocEnriched?.target_object?.label ??
    assocEnriched?.object_label ??
    'Associated object';

  const { targetEntityId } = assoc;

  // Gate the id passed to ActionEntityPreview the same way registry.helpers.tsx
  // gates entity ids for MaybeEntityBadge: a target with neither a CRM record
  // nor a covering preview overlay must never reach useCrmObjectSnapshot,
  // which would otherwise attempt a real ensureEntity fetch for an entity
  // that doesn't exist.
  const crmPreview = useCrmPreview();
  const resolvedTargetEntityId = resolvableEntityId(
    targetEntityId,
    assocEnriched?.target_object,
    crmPreview
  );

  // Proposed field values from the action payload flow through as
  // fallbackFlatProps so brand-new (non-CRM) targets still render content.
  const fallbackFlatProps = useMemo(
    () =>
      assocEnriched?.current_values
        ? extractProperties(assocEnriched.current_values)
        : undefined,
    [assocEnriched?.current_values]
  );

  const resolvedExcludedMessage =
    excludedMessage ?? `Won't be linked – ${targetLabel} is unchecked`;

  let target: ReactNode;
  if (isStageAssociation(assoc.associationType)) {
    // Same `excluded` treatment as ActionEntityPreview so an excluded stage
    // association is never visually identical to an applying one — the stage
    // badge itself has no excluded prop, so the wrapper carries the signal.
    target = (
      <ExcludedOverlay excluded={excluded} message={resolvedExcludedMessage}>
        <ActionStageBadge
          value={targetEntityId}
          orgId={orgId}
          entityType={sourceEntityType}
          fallbackLabel={targetLabel}
        />
      </ExcludedOverlay>
    );
  } else {
    const preview = (
      <ActionEntityPreview
        entityId={resolvedTargetEntityId}
        entityType={targetEntityType}
        label={targetLabel}
        enrichedData={{
          object_label: targetLabel,
          entity_type_name: targetEntityType,
          current_values: assocEnriched?.current_values,
        }}
        orgId={orgId}
        compact={compact}
        // The relationship label above the target already names the type, so
        // a type pill on the target itself would just repeat it. Not gated on
        // `compact`: a full card can collapse to the compact layout on its own
        // when it has no secondary fields, and would otherwise pick the pill
        // back up here — "Company" over a chip reading "Company".
        hideTypeBadge
        excluded={excluded}
        excludedMessage={resolvedExcludedMessage}
        fallbackFlatProps={fallbackFlatProps}
      />
    );
    if (compact) {
      target = preview;
    } else {
      // Registered content components render bare (no card chrome) for the
      // default variant, so wrap them in a bordered ObjectCard. The
      // unregistered fallback (EntityCard, returned by ActionEntityPreview)
      // carries its own border — wrapping it too would double-nest the card
      // border, so render it directly.
      target = ENTITY_CONTENT_REGISTRY[targetEntityType] ? (
        <ObjectCard bordered>{preview}</ObjectCard>
      ) : (
        preview
      );
    }
  }

  return (
    <div className='flex flex-col gap-1'>
      <span className='text-xs font-medium text-fg-tertiary'>
        {relationshipLabel}
      </span>
      {target}
    </div>
  );
}
